import React from 'react';

import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import {
  DesktopComputerIcon,
  ExclamationCircleIcon,
  HomeIcon
} from '@heroicons/react/outline';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  CoverageSection,
  FadeIn,
  FaqSection,
  Footer,
  GradientBg,
  Header,
  InsuranceSection,
  PageHeader,
  WhoNeedsInsuranceSection,
  WhyIsInsuranceImportant
} from '../components';
import { GeneralLiabilitySvg } from '../components/svg';

const faqs = [
  {
    question: 'Is general liability insurance required by law?',
    answer:
      'General liability requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a general liability policy is crucial to protect your business from everyday risks.'
  },
  {
    question: 'How do I show proof I have a general liability policy?',
    answer: `Once you've purchased your general liability policy, a Certificate of Insurance (“COI”) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance.`
  },
  {
    question: 'What is an additional insured?',
    answer: `An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party whom which you conduct business with.`
  },
  {
    question:
      'Does general liability cover my store, office or physical location?',
    answer: `Damage to your location is not protected under a general liability policy. If you would like to have additional coverage to protect your store, office, or physical location you will need to purchase a business owners’ policy. You can purchase one <a class="text-green-600 hover:underline cursor-pointer font-medium" href="/quote">here</a>.`
  }
];

const coverageItems = [
  {
    title: 'Bodily Injury',
    description:
      'In the event your customer (or another third-party) is injured on your property, you may be required to pay for medical expenses and damages.',
    icon: ExclamationCircleIcon
  },
  {
    title: 'Property Damage',
    description: `If your business accidentally damaged someone else's property, you may be required to cover the cost of repair or replacement.`,
    icon: HomeIcon
  },
  {
    title: 'Personal and Advertising Injury',
    description:
      'In the event your customer (or another third-party) claims that you damaged their reputation or plagiarized their intellectual property, regardless of if the claim holds any merit, you may need to retain a lawyer and cover additional legal expenses.',
    icon: DesktopComputerIcon
  }
];

const GeneralLiability: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="General liability"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'General liability'
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is general liability insurance required by law?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "General liability requirements vary by state and industry. However, your business can be exposed to high risks of financial loss if you are not properly protected. You may also lose potential clients and customers if you are asked to show proof of insurance and are not able to provide evidence of coverage. In short, although it might not be legally required, a general liability policy is crucial to protect your business from everyday risks."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I show proof I have a general liability policy?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Once you've purchased your general liability policy, a Certificate of Insurance (COI) will be sent to you and can be presented to any party that requires proof of coverage. You can also request that the COI be sent to any third-party that requests verification of insurance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What is an additional insured?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "An additional insured is any party that wants to be listed as a covered party under your policy. In certain cases, an additional insured might be a vendor, marketplace, landlord or any other third-party whom which you conduct business with."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Does general liability cover my store, office or physical location?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Damage to your location is not protected under a general liability policy. If you would like to have additional coverage to protect your store, office, or physical location you will need to purchase a business owners' policy."
                  }
                }
              ]
            })
          }}
        />
      </Head>
      <GradientBg />
      <Header />
      <PageHeader
        title="General liability"
        image={
          <>
            <GeneralLiabilitySvg className="hidden sm:block mx-auto max-w-4xl w-full mt-8 md:mt-0" />
            <img
              src="/img/sb1.jpg"
              className="sm:hidden w-full max-w-4xl shadow rounded overflow-hidden"
            />
          </>
        }
      />

      {/* Section-1 */}
      <div className="max-w-7xl mx-auto pb-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            What is <span className="text-primary">general liability</span>{' '}
            insurance?
          </h2>
        </FadeIn>
        <FadeIn>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            General liability insurance, also known as commercial general
            liability (CGL) insurance, provides financial protection from
            everyday workplace risks such as property damage, customer injury
            and lawsuits. It is the most common policy to have in place to
            provide overall protection for any sized business, across all
            industries.
          </p>
        </FadeIn>
      </div>

      {/* Section-2 */}
      <CoverageSection title="general liability" items={coverageItems} />

      {/* Section-3 */}
      <WhoNeedsInsuranceSection
        title="general liability"
        description="Every small business, regardless of size or industry, needs protection from common business risks in the event of a lawsuit, third-party injury, or property damage. You may also be required to show proof of general liability insurance to a client, customer, or marketplace to validate coverage."
      />

      <WhyIsInsuranceImportant>
        <FadeIn>
          <h2 className="text-5xl text-alt font-extrabold my-7">
            Why is <span className="text-primary">general liability</span>{' '}
            important?
          </h2>
        </FadeIn>

        <p className="mt-4 text-lg text-gray-500">
          Regardless of industry or size, a general liability policy can protect
          your business from common financial risks that can be incurred because
          of a third-party accident. Certain states and various clients require
          proof of general liability insurance to allow businesses to transact.
        </p>
      </WhyIsInsuranceImportant>

      <InsuranceSection />

      <FaqSection title="General liability insurance" faqs={faqs} />

      <BottomGraphic />
      <Footer />
    </>
  );
};

export default GeneralLiability;
